import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import {
  RestoreCartItem,
  TargetOrg,
  ValidationResult,
  RestorePlanSummary,
  Dependency,
} from '../api/types';
import { RestoreCart } from '../components/RestoreCart';

export function RestorePlan() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<RestoreCartItem[]>([]);
  const [targetOrgs, setTargetOrgs] = useState<TargetOrg[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load cart from sessionStorage
  const loadCart = useCallback(() => {
    const stored = sessionStorage.getItem('restore_cart');
    setCartItems(stored ? JSON.parse(stored) : []);
  }, []);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Load target orgs
  useEffect(() => {
    api.get<TargetOrg[]>('/orgs')
      .then(setTargetOrgs)
      .catch(() => {/* handled by validation */});
  }, []);

  function removeFromCart(recordId: string) {
    const updated = cartItems.filter((item) => item.recordId !== recordId);
    setCartItems(updated);
    sessionStorage.setItem('restore_cart', JSON.stringify(updated));
    // Clear validation if cart changes
    setValidation(null);
  }

  async function handleValidate() {
    if (!selectedOrg) {
      setError('Please select a target organization');
      return;
    }
    if (cartItems.length === 0) {
      setError('Add records to the restore cart first');
      return;
    }

    setIsValidating(true);
    setError(null);
    setValidation(null);

    try {
      const result = await api.post<ValidationResult>('/restore-plans', {
        records: cartItems,
        targetOrgId: selectedOrg,
        dryRun: true,
      });
      setValidation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setIsValidating(false);
    }
  }

  async function handleSubmitForApproval() {
    if (!validation?.isValid || !selectedOrg) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const plan = await api.post<RestorePlanSummary>('/restore-plans', {
        records: cartItems,
        targetOrgId: selectedOrg,
        dryRun: false,
      });
      // Clear cart on successful submission
      sessionStorage.removeItem('restore_cart');
      navigate(`/restore/approval/${plan.planId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit plan');
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderDependencyGraph(dependencies: Dependency[]) {
    if (dependencies.length === 0) {
      return <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>No dependencies detected.</p>;
    }

    return (
      <table aria-label="Dependency graph">
        <thead>
          <tr>
            <th scope="col">Source</th>
            <th scope="col">Relationship</th>
            <th scope="col">Target</th>
          </tr>
        </thead>
        <tbody>
          {dependencies.map((dep, idx) => (
            <tr key={idx}>
              <td>
                <code style={{ fontSize: '0.75rem' }}>{dep.sourceType}/{dep.sourceId.slice(0, 8)}</code>
              </td>
              <td>{dep.relationship}</td>
              <td>
                <code style={{ fontSize: '0.75rem' }}>{dep.targetType}/{dep.targetId.slice(0, 8)}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div>
      <h2>Restore Plan</h2>

      {/* Error */}
      {error && (
        <div className="card" role="alert" style={{ borderLeft: '4px solid var(--color-error)', marginBottom: '1rem' }}>
          <p style={{ color: 'var(--color-error)', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Cart */}
      <section aria-labelledby="cart-heading">
        <RestoreCart items={cartItems} onRemove={removeFromCart} />
      </section>

      {/* Target Org Selection */}
      {cartItems.length > 0 && (
        <section aria-labelledby="target-heading" style={{ marginTop: '1.5rem' }}>
          <h3 id="target-heading">Target Organization</h3>
          <div className="card">
            <div className="form-group" style={{ maxWidth: '400px', marginBottom: 0 }}>
              <label htmlFor="targetOrg">Restore to</label>
              <select
                id="targetOrg"
                value={selectedOrg}
                onChange={(e) => {
                  setSelectedOrg(e.target.value);
                  setValidation(null);
                }}
                aria-describedby="target-help"
              >
                <option value="">Select target organization…</option>
                {targetOrgs.map((org) => (
                  <option key={org.orgId} value={org.orgId}>
                    {org.name} ({org.environment}) — {org.instanceUrl}
                  </option>
                ))}
              </select>
              <p id="target-help" style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                Records will be restored to the selected organization.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Actions */}
      {cartItems.length > 0 && selectedOrg && (
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button
            onClick={handleValidate}
            disabled={isValidating}
            className="secondary"
          >
            {isValidating ? 'Validating…' : 'Validate (Dry Run)'}
          </button>
          {validation?.isValid && (
            <button
              onClick={handleSubmitForApproval}
              disabled={isSubmitting}
              className="success"
            >
              {isSubmitting ? 'Submitting…' : 'Submit for Approval'}
            </button>
          )}
        </div>
      )}

      {/* Validation Results */}
      {validation && (
        <section aria-labelledby="validation-heading" style={{ marginTop: '1.5rem' }}>
          <h3 id="validation-heading">Validation Results</h3>

          <div className="card" style={{ borderLeft: `4px solid ${validation.isValid ? 'var(--color-success)' : 'var(--color-error)'}` }}>
            <p style={{ fontWeight: 600, color: validation.isValid ? 'var(--color-success)' : 'var(--color-error)' }}>
              {validation.isValid ? '✓ Validation passed' : '✗ Validation failed'}
            </p>

            {/* Errors */}
            {validation.errors.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: 'var(--color-error)' }}>Errors ({validation.errors.length})</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {validation.errors.map((err, idx) => (
                    <li key={idx} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                      <code>{err.recordId}</code>
                      {err.field && <span> → {err.field}</span>}: {err.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {validation.warnings.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: 'var(--color-warning)' }}>Warnings ({validation.warnings.length})</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {validation.warnings.map((warn, idx) => (
                    <li key={idx} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                      <code>{warn.recordId}</code>: {warn.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Dependency Graph */}
          <div style={{ marginTop: '1rem' }}>
            <h4>Dependency Preview</h4>
            <div className="card">
              {renderDependencyGraph(validation.dependencyGraph)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
